import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import SidebarMenu from "../AdminPanel/SidebarMenu";
import TopBar from "../AdminPanel/TopBar";
import EditClient from "./EditClient";

const PRIMARY = "#1565C0";
const DARK_PRIMARY = "#1565C0";
const BLACK = "#111827";

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");

  for (let cookie of cookies) {
    const trimmed = cookie.trim();

    if (trimmed.startsWith(name + "=")) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }

  return null;
}

const money = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const emptyDash = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const getEstadoColor = (estado) => {
  const e = String(estado || "").toUpperCase();

  if (e.includes("AUTORIZADO") || e.includes("PAGADA")) return "#047857";
  if (e.includes("PENDIENTE") || e.includes("ENVIADA")) return "#92400e";
  if (e.includes("NO") || e.includes("ERROR") || e.includes("DEVUELTA") || e.includes("ANULADA")) return "#b91c1c";

  return "#334155";
};

const getRoleText = (persona) => {
  if (persona?.es_cliente && persona?.es_proveedor) return "Cliente y proveedor";
  if (persona?.es_proveedor) return "Proveedor";
  if (persona?.es_cliente) return "Cliente";
  return "Persona registrada";
};

const SectionTitle = ({ title, subtitle }) => (
  <Box sx={{ mb: 1.8 }}>
    <Typography sx={{ fontWeight: 800, fontSize: 18, color: BLACK }}>
      {title}
    </Typography>

    {subtitle && (
      <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.3 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const InfoRow = ({ label, value, strong = false }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      gap: 2,
      py: 0.8,
      borderBottom: "1px solid #e5e7eb",
    }}
  >
    <Typography sx={{ fontSize: 13, color: "#64748b" }}>
      {label}
    </Typography>

    <Typography
      sx={{
        fontSize: 13,
        color: BLACK,
        fontWeight: strong ? 800 : 600,
        textAlign: "right",
      }}
    >
      {emptyDash(value)}
    </Typography>
  </Box>
);

const SummaryCard = ({ label, value, tone = "default" }) => {
  const color =
    tone === "danger"
      ? "#b91c1c"
      : tone === "success"
      ? "#047857"
      : tone === "warning"
      ? "#92400e"
      : BLACK;

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: "1px solid #e5e7eb",
        boxShadow: "none",
        bgcolor: "#fff",
        height: "100%",
      }}
    >
      <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>
        {label}
      </Typography>

      <Typography sx={{ mt: 0.8, fontSize: 22, fontWeight: 900, color }}>
        {value}
      </Typography>
    </Paper>
  );
};

const TableShell = ({
  children,
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}) => (
  <>
    <TableContainer
      sx={{
        border: "1px solid #e5e7eb",
        borderRadius: 2.5,
        overflow: "hidden",
      }}
    >
      <Table size="small">{children}</Table>
    </TableContainer>

    <TablePagination
      component="div"
      count={count}
      page={page}
      onPageChange={onPageChange}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={onRowsPerPageChange}
      rowsPerPageOptions={[5, 10, 25, 50]}
      labelRowsPerPage="Filas por página"
      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      sx={{
        border: "1px solid #e5e7eb",
        borderTop: "0",
        borderRadius: "0 0 10px 10px",
        ".MuiTablePagination-toolbar": {
          minHeight: 48,
        },
        ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
          fontSize: 13,
          color: "#475569",
        },
      }}
    />
  </>
);

const headCellSx = {
  bgcolor: DARK_PRIMARY,
  color: "#fff",
  fontWeight: 800,
  fontSize: 13,
  py: 1.2,
  borderBottom: "0",
};

const bodyCellSx = {
  fontSize: 13,
  color: "#334155",
  borderBottom: "1px solid #eef2f7",
};

const FinancialBlock = ({ title, subtitle, type, data }) => {
  const resumen = data?.resumen || {};
  const saldos = data?.saldos || [];
  const documentos = data?.documentos || [];
  const movimientos = data?.movimientos || [];
  const pagos = data?.pagos || [];

  const [docsPage, setDocsPage] = useState(0);
  const [docsRows, setDocsRows] = useState(10);
  const [movPage, setMovPage] = useState(0);
  const [movRows, setMovRows] = useState(10);

  const docsVisible = documentos.slice(
    docsPage * docsRows,
    docsPage * docsRows + docsRows
  );

  const movVisible = movimientos.slice(
    movPage * movRows,
    movPage * movRows + movRows
  );

  const totalBase =
    type === "proveedor" ? resumen.total_comprado : resumen.total_facturado;

  const totalBaseLabel = type === "proveedor" ? "Comprado" : "Vendido";
  const saldoLabel = type === "proveedor" ? "Por pagar" : "Por cobrar";

  return (
    <Box sx={{ mt: 0 }}>
      <Paper
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid #e5e7eb",
          boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
          mb: 2.5,
        }}
      >
        <SectionTitle title={title} subtitle={subtitle} />

        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={2.4}>
            <SummaryCard label="Documentos" value={resumen.total_documentos || 0} />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <SummaryCard label={totalBaseLabel} value={`$ ${money(totalBase)}`} />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <SummaryCard label="Pagado" value={`$ ${money(resumen.total_pagado)}`} tone="success" />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <SummaryCard
              label={saldoLabel}
              value={`$ ${money(resumen.total_saldo)}`}
              tone={Number(resumen.total_saldo || 0) > 0 ? "danger" : "success"}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <SummaryCard
              label="Crédito"
              value={`$ ${money(resumen.credito_cartera)}`}
              tone={Number(resumen.credito_cartera || 0) > 0 ? "warning" : "default"}
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid #e5e7eb",
              boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
              mb: 2.5,
            }}
          >
            <SectionTitle
              title="Documentos"
              subtitle={type === "proveedor" ? "Compras registradas a este proveedor" : "Ventas registradas a esta persona"}
            />

            <TableShell
              count={documentos.length}
              page={docsPage}
              rowsPerPage={docsRows}
              onPageChange={(_e, newPage) => setDocsPage(newPage)}
              onRowsPerPageChange={(e) => {
                setDocsRows(parseInt(e.target.value, 10));
                setDocsPage(0);
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Fecha</TableCell>
                  <TableCell sx={headCellSx}>Documento</TableCell>
                  <TableCell sx={headCellSx}>Estado</TableCell>
                  <TableCell align="right" sx={headCellSx}>Total</TableCell>
                  <TableCell align="right" sx={headCellSx}>Pagado</TableCell>
                  <TableCell align="right" sx={headCellSx}>Saldo</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {docsVisible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={bodyCellSx}>
                      <Typography sx={{ py: 2, color: "#64748b", fontSize: 13 }}>
                        No hay documentos registrados.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {docsVisible.map((doc) => (
                  <TableRow
                    key={`${type}-doc-${doc.id}`}
                    hover
                    sx={{
                      "&:hover": {
                        bgcolor: "rgba(21,101,192,0.06)",
                      },
                    }}
                  >
                    <TableCell sx={bodyCellSx}>{emptyDash(doc.fecha_emision)}</TableCell>

                    <TableCell sx={bodyCellSx}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: BLACK }}>
                        {emptyDash(doc.numero_documento)}
                      </Typography>

                      <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                        {emptyDash(doc.tipo_documento_label || doc.tipo_documento)}
                      </Typography>
                    </TableCell>

                    <TableCell sx={bodyCellSx}>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 800,
                          color: getEstadoColor(doc.estado || doc.sri_estado),
                        }}
                      >
                        {emptyDash(doc.estado || doc.sri_estado)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right" sx={bodyCellSx}>
                      $ {money(doc.total)}
                    </TableCell>

                    <TableCell align="right" sx={bodyCellSx}>
                      $ {money(doc.pagado_total)}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        ...bodyCellSx,
                        fontWeight: 800,
                        color: Number(doc.saldo || 0) > 0 ? "#b91c1c" : "#047857",
                      }}
                    >
                      $ {money(doc.saldo)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableShell>
          </Paper>

          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid #e5e7eb",
              boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
            }}
          >
            <SectionTitle
              title="Movimientos de cartera"
              subtitle={type === "proveedor" ? "Registro de obligaciones y pagos al proveedor" : "Registro de cobros, créditos y deudas del cliente"}
            />

            <TableShell
              count={movimientos.length}
              page={movPage}
              rowsPerPage={movRows}
              onPageChange={(_e, newPage) => setMovPage(newPage)}
              onRowsPerPageChange={(e) => {
                setMovRows(parseInt(e.target.value, 10));
                setMovPage(0);
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Fecha</TableCell>
                  <TableCell sx={headCellSx}>Origen</TableCell>
                  <TableCell sx={headCellSx}>Efecto</TableCell>
                  <TableCell sx={headCellSx}>Documento</TableCell>
                  <TableCell align="right" sx={headCellSx}>Valor</TableCell>
                  <TableCell align="right" sx={headCellSx}>Deuda final</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {movVisible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={bodyCellSx}>
                      <Typography sx={{ py: 2, color: "#64748b", fontSize: 13 }}>
                        No hay movimientos registrados.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {movVisible.map((mov) => (
                  <TableRow
                    key={`${type}-mov-${mov.id}`}
                    hover
                    sx={{
                      "&:hover": {
                        bgcolor: "rgba(21,101,192,0.06)",
                      },
                    }}
                  >
                    <TableCell sx={bodyCellSx}>{emptyDash(mov.fecha)}</TableCell>

                    <TableCell sx={bodyCellSx}>
                      {emptyDash(mov.origen_label || mov.origen)}
                    </TableCell>

                    <TableCell sx={bodyCellSx}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: BLACK }}>
                        {emptyDash(mov.efecto_label || mov.efecto)}
                      </Typography>
                    </TableCell>

                    <TableCell sx={bodyCellSx}>
                      {emptyDash(mov.documento_numero)}
                    </TableCell>

                    <TableCell align="right" sx={bodyCellSx}>
                      $ {money(mov.valor)}
                    </TableCell>

                    <TableCell align="right" sx={{ ...bodyCellSx, fontWeight: 800 }}>
                      $ {money(mov.deuda_resultante)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableShell>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid #e5e7eb",
              boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
              mb: 2.5,
            }}
          >
            <SectionTitle title="Saldos" />

            {saldos.length === 0 && (
              <Typography sx={{ color: "#64748b", fontSize: 13 }}>
                No hay saldos registrados.
              </Typography>
            )}

            {saldos.map((saldo, index) => (
              <Box key={`${type}-saldo-${saldo.rol}-${index}`} sx={{ mb: 1.5 }}>
                <Typography sx={{ fontWeight: 800, color: BLACK, mb: 0.5 }}>
                  {emptyDash(saldo.rol_label || saldo.rol)}
                </Typography>

                <InfoRow label={type === "proveedor" ? "Por pagar" : "Por cobrar"} value={`$ ${money(saldo.deuda)}`} strong />
                <InfoRow label="Crédito" value={`$ ${money(saldo.credito)}`} />
              </Box>
            ))}
          </Paper>

          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid #e5e7eb",
              boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
            }}
          >
            <SectionTitle title={type === "proveedor" ? "Pagos realizados" : "Cobros registrados"} />

            {pagos.length === 0 && (
              <Typography sx={{ color: "#64748b", fontSize: 13 }}>
                No hay pagos registrados.
              </Typography>
            )}

            {pagos.map((pago, index) => (
              <Box key={`${type}-pago-${pago.id}-${pago.documento_id}-${index}`} sx={{ mb: 1.5 }}>
                <Typography sx={{ fontWeight: 900, color: BLACK }}>
                  $ {money(pago.valor)}
                </Typography>

                <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.3 }}>
                  {emptyDash(pago.forma)} · {emptyDash(pago.fecha)}
                </Typography>

                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                  Doc: {emptyDash(pago.documento_numero)}
                </Typography>

                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                  Cuenta: {emptyDash(pago.cuenta)}
                </Typography>

                <Divider sx={{ mt: 1.3 }} />
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const [formData, setFormData] = useState({
    tipo: "N",
    razon_social: "",
    nombre_comercial: "",
    cedula: "",
    ruc: "",
    telefono: "",
    correo: "",
    direccion: "",
    es_cliente: false,
    es_proveedor: false,
    es_empleado: false,
    es_extranjero: false,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const persona = data?.persona || {};
  const cliente = data?.cliente || {};
  const proveedor = data?.proveedor || {};

  const hasCliente = !!persona.es_cliente;
  const hasProveedor = !!persona.es_proveedor;

  const availableTabs = useMemo(() => {
    return [
      {
        key: "administrar",
        label: "Administrar",
        disabled: false,
      },
      {
        key: "cliente",
        label: "Como cliente",
        disabled: !hasCliente,
      },
      {
        key: "proveedor",
        label: "Como proveedor",
        disabled: !hasProveedor,
      },
    ];
  }, [hasCliente, hasProveedor]);

  const syncForm = (personaData) => {
    setFormData({
      tipo: personaData?.tipo || "N",
      razon_social: personaData?.razon_social || "",
      nombre_comercial: personaData?.nombre_comercial || "",
      cedula: personaData?.cedula || "",
      ruc: personaData?.ruc || "",
      telefono: personaData?.telefono || "",
      correo: personaData?.correo || "",
      direccion: personaData?.direccion || "",
      es_cliente: !!personaData?.es_cliente,
      es_proveedor: !!personaData?.es_proveedor,
      es_empleado: !!personaData?.es_empleado,
      es_extranjero: !!personaData?.es_extranjero,
    });
  };

  const fetchDashboard = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`/clientes/dashboard/${id}/`, {
        credentials: "include",
      });

      const json = await res.json();

      if (!res.ok || !json.ok) {
        setError(json.error || "No se pudo cargar la información de la persona.");
        setData(null);
        return;
      }

      setData(json);
      syncForm(json.persona || {});
    } catch {
      setError("Error de conexión al cargar la información de la persona.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [id]);

  const handleSave = async () => {
    if (saving) return;

    setSaving(true);

    const payload = {
      tipo: String(formData.tipo || "N").toUpperCase(),
      razon_social: String(formData.razon_social || "").toUpperCase(),
      nombre_comercial: String(formData.nombre_comercial || "").toUpperCase(),
      cedula: String(formData.cedula || "").trim(),
      ruc: String(formData.ruc || "").trim(),
      telefono: String(formData.telefono || "").trim(),
      correo: String(formData.correo || "").trim(),
      direccion: String(formData.direccion || "").trim(),
      es_cliente: !!formData.es_cliente,
      es_proveedor: !!formData.es_proveedor,
      es_empleado: !!formData.es_empleado,
      es_extranjero: !!formData.es_extranjero,
    };

    try {
      const csrf = getCSRFToken();

      const res = await fetch(`/clientes/editar/${id}/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRFToken": csrf } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setSnackbar({
          open: true,
          type: "error",
          message: json.error || "No se pudo actualizar la persona.",
        });
        return;
      }

      setSnackbar({
        open: true,
        type: "success",
        message: json.mensaje || "Persona actualizada correctamente.",
      });

      await fetchDashboard();
    } catch {
      setSnackbar({
        open: true,
        type: "error",
        message: "Error de conexión al actualizar la persona.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", {
        method: "POST",
        credentials: "include",
      });
    } catch {}

    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/admin-panel/clientes")}
            sx={{
              textTransform: "none",
              backgroundColor: PRIMARY,
              borderRadius: 2,
              px: 2.2,
              fontWeight: 700,
              mb: 2,
              boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
              "&:hover": {
                backgroundColor: "#0D47A1",
              },
            }}
          >
            Regresar
          </Button>

          <Paper
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
              bgcolor: "#fff",
            }}
          >
            {loading ? (
              <Box
                sx={{
                  minHeight: 360,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {!error && data && (
                  <>
                    <Box sx={{ p: 3, bgcolor: "#fff" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: 20, md: 25 },
                              color: "#0f172a",
                              lineHeight: 1.2,
                            }}
                          >
                            {persona.razon_social || "Persona"}
                          </Typography>

                          <Typography sx={{ color: "#64748b", fontSize: 13, mt: 0.8 }}>
                            {persona.ruc
                              ? `RUC: ${persona.ruc}`
                              : persona.cedula
                              ? `Cédula: ${persona.cedula}`
                              : "Sin identificación disponible"}
                          </Typography>

                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5 }}>
                            {persona.es_cliente && (
                              <Box
                                sx={{
                                  px: 1.3,
                                  py: 0.45,
                                  borderRadius: 2,
                                  bgcolor: "rgba(21,101,192,0.10)",
                                  color: PRIMARY,
                                  fontSize: 12,
                                  fontWeight: 800,
                                }}
                              >
                                Cliente
                              </Box>
                            )}

                            {persona.es_proveedor && (
                              <Box
                                sx={{
                                  px: 1.3,
                                  py: 0.45,
                                  borderRadius: 2,
                                  bgcolor: "rgba(4,120,87,0.10)",
                                  color: "#047857",
                                  fontSize: 12,
                                  fontWeight: 800,
                                }}
                              >
                                Proveedor
                              </Box>
                            )}

                            {persona.es_empleado && (
                              <Box
                                sx={{
                                  px: 1.3,
                                  py: 0.45,
                                  borderRadius: 2,
                                  bgcolor: "rgba(146,64,14,0.10)",
                                  color: "#92400e",
                                  fontSize: 12,
                                  fontWeight: 800,
                                }}
                              >
                                Empleado
                              </Box>
                            )}

                            {persona.es_extranjero && (
                              <Box
                                sx={{
                                  px: 1.3,
                                  py: 0.45,
                                  borderRadius: 2,
                                  bgcolor: "rgba(100,116,139,0.12)",
                                  color: "#475569",
                                  fontSize: 12,
                                  fontWeight: 800,
                                }}
                              >
                                Extranjero
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        mx: 3,
                        mt: 2,
                        backgroundColor: PRIMARY,
                        borderTopLeftRadius: "14px",
                        borderTopRightRadius: "14px",
                        overflow: "hidden",
                      }}
                    >
                      <Tabs
                        value={tabValue}
                        onChange={(_e, value) => setTabValue(value)}
                        variant="scrollable"
                        scrollButtons={false}
                        sx={{
                          minHeight: "52px",
                          "& .MuiTab-root": {
                            color: "#fff",
                            textTransform: "none",
                            fontWeight: 600,
                            minHeight: "52px",
                            borderTopLeftRadius: "10px",
                            borderTopRightRadius: "10px",
                            transition: "all 0.2s ease",
                          },
                          "& .MuiTab-root.Mui-disabled": {
                            color: "rgba(255,255,255,0.45)",
                          },
                          "& .Mui-selected": {
                            backgroundColor: "#fff",
                            color: PRIMARY,
                            borderTop: `4px solid ${PRIMARY}`,
                            borderLeft: `4px solid ${PRIMARY}`,
                            borderRight: `4px solid ${PRIMARY}`,
                            borderBottom: "none",
                            marginTop: "5px",
                            marginLeft: "10px",
                          },
                          "& .MuiTabs-indicator": {
                            display: "none",
                          },
                        }}
                      >
                        {availableTabs.map((tab) => (
                          <Tab key={tab.key} label={tab.label} disabled={tab.disabled} />
                        ))}
                      </Tabs>
                    </Box>

                    <Box sx={{ p: 3 }}>
                      {tabValue === 0 && (
                        <EditClient
                          personaId={id}
                          persona={persona}
                          formData={formData}
                          setFormData={setFormData}
                          saving={saving}
                          onSave={handleSave}
                          onRefresh={fetchDashboard}
                        />
                      )}

                      {tabValue === 1 && hasCliente && (
                        <FinancialBlock
                          title="Relación como cliente"
                          subtitle="Valores generados por ventas realizadas a esta persona"
                          type="cliente"
                          data={cliente}
                        />
                      )}

                      {tabValue === 1 && !hasCliente && (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          Esta persona no está marcada como cliente.
                        </Alert>
                      )}

                      {tabValue === 2 && hasProveedor && (
                        <FinancialBlock
                          title="Relación como proveedor"
                          subtitle="Valores generados por compras realizadas a esta persona"
                          type="proveedor"
                          data={proveedor}
                        />
                      )}

                      {tabValue === 2 && !hasProveedor && (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          Esta persona no está marcada como proveedor.
                        </Alert>
                      )}
                    </Box>
                  </>
                )}

                {error && (
                  <Box sx={{ p: 3 }}>
                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                      {error}
                    </Alert>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientDetail;